import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, Loader2, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_EMAIL = 'lucia@varimesrdcom.sk';

export default function LoginPage() {
  const { t, user, setUser, language } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [membershipExpired, setMembershipExpired] = useState(false);

  // If already logged in, redirect
  React.useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email || !password) {
      setErrorMessage(
        language === 'sk'
          ? 'Vyplňte prosím email a heslo.'
          : 'Vyplňte prosím email a heslo.'
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        // Try to detect specific membership-expired error (403 with structured detail)
        let isMembershipExpired = false;
        let membershipMessage = '';
        try {
          const errData = await response.json();
          const detail = errData?.detail;
          if (response.status === 403 && detail && typeof detail === 'object' && detail.code === 'membership_expired') {
            isMembershipExpired = true;
            membershipMessage = detail.message || '';
          }
        } catch (_) {
          // ignore JSON parse errors — fall back to generic message
        }

        if (isMembershipExpired) {
          setErrorMessage(
            membershipMessage ||
              (language === 'sk'
                ? 'Vaše členstvo vypršalo alebo nie je aktívne. Prosím obnovte si predplatné.'
                : 'Vaše členství vypršelo nebo není aktivní. Prosím obnovte si předplatné.')
          );
          setMembershipExpired(true);
        } else {
          // Generic message for security, keep support hint
          setErrorMessage(
            language === 'sk'
              ? 'Nesprávny email alebo heslo.'
              : 'Nesprávný email nebo heslo.'
          );
          setMembershipExpired(false);
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      setUser(data.user);
      toast.success(language === 'sk' ? 'Úspešne prihlásené!' : 'Úspěšně přihlášeno!');
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage(
        language === 'sk'
          ? 'Nesprávny email alebo heslo.'
          : 'Nesprávný email nebo heslo.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-subtle flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl p-8 shadow-hover border border-border/40">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center mx-auto mb-4">
              <span className="font-serif text-3xl font-bold text-white">L</span>
            </div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-heading">
              {t('welcomeBack')}
            </h1>
            <p className="text-muted-foreground mt-2">
              S Luciou v kuchyni
            </p>
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
            {/* Inline error panel */}
            {errorMessage && (
              <div
                className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3"
                data-testid="login-error-panel"
                role="alert"
              >
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700 leading-relaxed">
                  <p className="font-medium mb-1" data-testid="login-error-message">{errorMessage}</p>
                  {membershipExpired && (
                    <div className="mt-2" data-testid="login-renew-cta">
                      <a
                        href="https://varimesrdcom.sk/objednavka"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-white font-medium text-sm hover:bg-primary-hover transition-colors"
                        data-testid="login-renew-membership-link"
                      >
                        <Sparkles className="w-4 h-4" />
                        {language === 'sk' ? 'Obnoviť členstvo' : 'Obnovit členství'}
                      </a>
                    </div>
                  )}
                  <p className="text-red-600/90 mt-2">
                    {language === 'sk'
                      ? 'V prípade problémov s prihlásením napíšte na '
                      : 'V případě problémů s přihlášením napište na '}
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="underline font-medium hover:text-red-800"
                      data-testid="login-support-email"
                    >
                      {SUPPORT_EMAIL}
                    </a>
                    .
                  </p>
                </div>
              </div>
            )}

            {/* Email field */}
            <div>
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vas@email.sk"
                  className="pl-10 py-5 rounded-xl"
                  data-testid="email-input"
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password field */}
            <div>
              <Label htmlFor="password" className="text-sm font-medium">
                {language === 'sk' ? 'Heslo' : 'Heslo'}
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 py-5 rounded-xl"
                  data-testid="password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 mt-2"
              data-testid="submit-login"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {language === 'sk' ? 'Prihlasujem...' : 'Přihlašuji...'}
                </>
              ) : (
                language === 'sk' ? 'Prihlásiť sa' : 'Přihlásit se'
              )}
            </Button>

            {/* Forgot password */}
            <div className="text-center mt-3">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-primary hover:text-primary-hover hover:underline"
                data-testid="forgot-password-link"
              >
                {language === 'sk' ? 'Zabudli ste heslo?' : 'Zapomněli jste heslo?'}
              </button>
            </div>
          </form>

          {/* Buy membership CTA - for non-members */}
          <div className="pt-6 mt-6 border-t border-border/40 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              {language === 'sk'
                ? 'Ešte nie ste členka?'
                : 'Ještě nejste členka?'}
            </p>
            <a
              href="https://varimesrdcom.sk/objednavka"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:text-primary-hover font-medium transition-colors"
              data-testid="login-buy-membership-link"
            >
              <Sparkles className="w-4 h-4" />
              {language === 'sk' ? 'Kúpiť členstvo' : 'Koupit členství'}
            </a>
          </div>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            {language === 'sk'
              ? 'Prihlásením súhlasíte s podmienkami používania.'
              : 'Přihlášením souhlasíte s podmínkami používání.'
            }
          </p>
        </div>
      </div>
    </div>
  );
}
